import { useState } from "react";
import { runWhatIf, WhatIfResult, FeatureDefinition } from "../api";

interface Props {
  feature?: FeatureDefinition | null;
  collectedFeatures?: FeatureDefinition[];
}

export default function WhatIfPanel({ feature, collectedFeatures = [] }: Props) {
  const [sigma, setSigma] = useState(2.0);
  const [direction, setDirection] = useState<"up" | "down">("up");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<WhatIfResult | null>(null);
  const [selectedFeatureId, setSelectedFeatureId] = useState<string>(feature?.feature_id || "");

  const allFeatures = feature
    ? [feature, ...collectedFeatures.filter(f => f.feature_id !== feature.feature_id)]
    : collectedFeatures;

  const activeFeature = allFeatures.find(f => f.feature_id === selectedFeatureId) || allFeatures[0];

  const handleRun = async () => {
    if (!activeFeature) return;
    setLoading(true);
    try {
      const res = await runWhatIf(activeFeature.feature_id, sigma, direction);
      setResult(res.data);
    } catch {
      // Use demo result
      setResult(DEMO_RESULT(activeFeature, sigma, direction));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", background: "#fff" }}>
      {/* Header */}
      <div style={{ padding: "20px 32px 14px", borderBottom: "1px solid rgba(0,0,0,0.06)", flexShrink: 0 }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
          What-If Simulation
          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 10px", borderRadius: 20, background: "rgba(139,92,246,0.1)", color: "#8b5cf6" }}>
            Stress Test
          </span>
        </h2>
        <p style={{ fontSize: 11, color: "rgba(0,0,0,0.45)", margin: "3px 0 0" }}>
          Perturb a variable and see ripple effects across the model
        </p>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "16px 32px 40px" }}>
        {/* Controls */}
        <div style={{
          padding: "16px 20px", borderRadius: 12,
          border: "1px solid rgba(0,0,0,0.06)", marginBottom: 16,
          background: "rgba(139,92,246,0.02)",
        }}>
          {/* Variable selector */}
          {allFeatures.length > 0 ? (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(0,0,0,0.35)", marginBottom: 6 }}>
                Variable to Perturb
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {allFeatures.map(f => (
                  <button key={f.feature_id} onClick={() => setSelectedFeatureId(f.feature_id)} style={{
                    padding: "6px 14px", borderRadius: 8, border: `1.5px solid ${selectedFeatureId === f.feature_id ? "#8b5cf6" : "rgba(0,0,0,0.08)"}`,
                    background: selectedFeatureId === f.feature_id ? "rgba(139,92,246,0.08)" : "transparent",
                    cursor: "pointer", fontSize: 11, fontWeight: 600,
                    color: selectedFeatureId === f.feature_id ? "#8b5cf6" : "rgba(0,0,0,0.6)",
                    transition: "all 0.15s ease",
                  }}>
                    {f.name.replace(/_/g, " ")}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: "rgba(0,0,0,0.4)", marginBottom: 14 }}>
              No features available — extract variables from an interview first
            </div>
          )}

          {/* Sigma slider */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(0,0,0,0.35)" }}>
                Perturbation Magnitude
              </span>
              <span style={{ fontSize: 13, fontWeight: 800, fontFamily: "'IBM Plex Mono', monospace", color: "#8b5cf6" }}>
                {direction === "up" ? "+" : "-"}{sigma.toFixed(1)}σ
              </span>
            </div>
            <input
              type="range" min={0.5} max={4} step={0.5} value={sigma}
              onChange={e => setSigma(parseFloat(e.target.value))}
              style={{ width: "100%", accentColor: "#8b5cf6" }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "rgba(0,0,0,0.25)" }}>
              <span>Mild (0.5σ)</span>
              <span>Extreme (4σ)</span>
            </div>
          </div>

          {/* Direction toggle */}
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            {(["up", "down"] as const).map(d => (
              <button key={d} onClick={() => setDirection(d)} style={{
                flex: 1, padding: "10px", borderRadius: 8, cursor: "pointer",
                border: `1.5px solid ${direction === d ? (d === "up" ? "#10b981" : "#ef4444") : "rgba(0,0,0,0.06)"}`,
                background: direction === d ? (d === "up" ? "rgba(16,185,129,0.06)" : "rgba(239,68,68,0.06)") : "transparent",
                fontSize: 12, fontWeight: 700,
                color: direction === d ? (d === "up" ? "#10b981" : "#ef4444") : "rgba(0,0,0,0.4)",
                transition: "all 0.15s ease",
              }}>
                {d === "up" ? "Increase" : "Decrease"}
              </button>
            ))}
          </div>

          <button onClick={handleRun} disabled={loading || !activeFeature} style={{
            width: "100%", padding: "12px", borderRadius: 8, border: "none", cursor: "pointer",
            background: activeFeature ? "#000" : "rgba(0,0,0,0.1)",
            color: activeFeature ? "#fff" : "rgba(0,0,0,0.3)",
            fontSize: 13, fontWeight: 700, transition: "all 0.15s ease",
          }}>
            {loading ? "Running simulation..." : "Run What-If"}
          </button>
        </div>

        {/* Results */}
        {result && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {/* Summary cards */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <div style={{ padding: "14px 16px", borderRadius: 10, background: "rgba(0,0,0,0.02)", textAlign: "center" }}>
                <div style={{ fontSize: 9, color: "rgba(0,0,0,0.35)", fontWeight: 700, textTransform: "uppercase" }}>Original Mean</div>
                <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "'IBM Plex Mono', monospace", marginTop: 4 }}>
                  {result.original_mean.toFixed(3)}
                </div>
              </div>
              <div style={{ padding: "14px 16px", borderRadius: 10, background: "rgba(139,92,246,0.04)", textAlign: "center" }}>
                <div style={{ fontSize: 9, color: "#8b5cf6", fontWeight: 700, textTransform: "uppercase" }}>Perturbed Mean</div>
                <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "'IBM Plex Mono', monospace", marginTop: 4, color: "#8b5cf6" }}>
                  {result.perturbed_mean.toFixed(3)}
                </div>
              </div>
              <div style={{
                padding: "14px 16px", borderRadius: 10, textAlign: "center",
                background: result.auc_delta >= 0 ? "rgba(16,185,129,0.04)" : "rgba(239,68,68,0.04)",
              }}>
                <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: result.auc_delta >= 0 ? "#10b981" : "#ef4444" }}>
                  AUC Delta
                </div>
                <div style={{
                  fontSize: 20, fontWeight: 800, fontFamily: "'IBM Plex Mono', monospace", marginTop: 4,
                  color: result.auc_delta >= 0 ? "#10b981" : "#ef4444",
                }}>
                  {result.auc_delta >= 0 ? "+" : ""}{(result.auc_delta * 100).toFixed(2)}%
                </div>
              </div>
            </div>

            {/* Ripple effects table */}
            <div style={{
              border: "1px solid rgba(0,0,0,0.06)", borderRadius: 12, overflow: "hidden",
            }}>
              <div style={{
                padding: "12px 16px", background: "rgba(0,0,0,0.02)",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", gap: 8,
              }}>
                Ripple Effects
                <span style={{ fontSize: 9, fontWeight: 600, color: "rgba(0,0,0,0.35)" }}>
                  How other coefficients shift when {result.feature_name?.replace(/_/g, " ")} changes
                </span>
              </div>

              {result.ripple_effects.map((effect, idx) => {
                const isPositive = effect.delta >= 0;
                const magnitude = Math.abs(effect.pct_change);
                const barWidth = Math.min(100, magnitude * 2);
                return (
                  <div key={idx} style={{
                    padding: "10px 16px",
                    borderBottom: idx < result.ripple_effects.length - 1 ? "1px solid rgba(0,0,0,0.04)" : "none",
                    display: "flex", alignItems: "center", gap: 12,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{effect.feature.replace(/_/g, " ")}</div>
                      <div style={{ fontSize: 10, color: "rgba(0,0,0,0.35)", fontFamily: "'IBM Plex Mono', monospace" }}>
                        {effect.baseline_coeff.toFixed(4)} → {effect.perturbed_coeff.toFixed(4)}
                      </div>
                    </div>

                    {/* Mini bar */}
                    <div style={{ width: 80, display: "flex", alignItems: "center", gap: 4 }}>
                      <div style={{ flex: 1, height: 6, background: "rgba(0,0,0,0.04)", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{
                          height: "100%", borderRadius: 3,
                          width: `${barWidth}%`,
                          background: isPositive ? "#10b981" : "#ef4444",
                          transition: "width 0.3s ease",
                        }} />
                      </div>
                    </div>

                    <div style={{
                      fontSize: 11, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", minWidth: 60, textAlign: "right",
                      color: isPositive ? "#10b981" : "#ef4444",
                    }}>
                      {isPositive ? "+" : ""}{effect.pct_change.toFixed(1)}%
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Interpretation */}
            <div style={{
              padding: "14px 18px", borderRadius: 10,
              background: "rgba(139,92,246,0.03)", border: "1px solid rgba(139,92,246,0.1)",
            }}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "#8b5cf6", marginBottom: 6 }}>
                Interpretation
              </div>
              <div style={{ fontSize: 11.5, lineHeight: 1.7, color: "rgba(0,0,0,0.6)" }}>
                {Math.abs(result.auc_delta) < 0.001
                  ? `The model is robust to a ${sigma}σ ${direction === "up" ? "increase" : "decrease"} in ${result.feature_name?.replace(/_/g, " ")}. Coefficient shifts are minimal.`
                  : result.auc_delta > 0
                  ? `A ${sigma}σ ${direction} shift in ${result.feature_name?.replace(/_/g, " ")} improves model performance by ${(result.auc_delta * 100).toFixed(2)}% AUC. This suggests the model could benefit from stronger signal on this variable.`
                  : `A ${sigma}σ ${direction} shift in ${result.feature_name?.replace(/_/g, " ")} degrades model performance by ${Math.abs(result.auc_delta * 100).toFixed(2)}% AUC. The model is sensitive to this variable — validate expert priors carefully.`
                }
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DEMO_RESULT(feature: FeatureDefinition, sigma: number, direction: string): WhatIfResult {
  const mean = feature.bayesian_prior.mean;
  const std = feature.bayesian_prior.std_dev;
  const shift = direction === "up" ? sigma * std : -sigma * std;
  return {
    feature_id: feature.feature_id,
    feature_name: feature.name,
    original_mean: mean,
    perturbed_mean: mean + shift,
    perturbation_sigma: sigma,
    direction,
    baseline_auc: 0.724,
    perturbed_auc: 0.724 + (direction === "up" ? 0.008 : -0.012) * sigma,
    auc_delta: (direction === "up" ? 0.008 : -0.012) * sigma,
    ripple_effects: [
      { feature: "Study_Hours", baseline_coeff: 0.2841, perturbed_coeff: 0.2841 + 0.015 * sigma * (direction === "up" ? 1 : -1), delta: 0.015 * sigma, pct_change: 5.3 * sigma },
      { feature: "Household_Income", baseline_coeff: 0.1923, perturbed_coeff: 0.1923 - 0.008 * sigma, delta: -0.008 * sigma, pct_change: -4.2 * sigma },
      { feature: "Sleep_Quality", baseline_coeff: 0.1456, perturbed_coeff: 0.1456 + 0.003 * sigma, delta: 0.003 * sigma, pct_change: 2.1 * sigma },
      { feature: "Class_Attendance", baseline_coeff: 0.2312, perturbed_coeff: 0.2312 - 0.011 * sigma, delta: -0.011 * sigma, pct_change: -4.8 * sigma },
      { feature: "Prior_GPA", baseline_coeff: 0.3102, perturbed_coeff: 0.3102 + 0.006 * sigma, delta: 0.006 * sigma, pct_change: 1.9 * sigma },
    ],
    baseline_posterior: { posterior_mean: mean, posterior_std: std },
    perturbed_posterior: { posterior_mean: mean + shift, posterior_std: std },
  };
}
